import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Loader2,
  MapPin,
  RefreshCw,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CoBrokerScorePill } from "@/components/CoBrokerScorePill";
import { UpgradeModal } from "@/components/UpgradeModal";
import { SkeletonCard } from "@/components/Skeleton";
import { formatPhpCompact } from "@/lib/format";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const COLUMNS = [
  { key: "Pending", label: "Pending", accent: "border-gray-200" },
  { key: "Inquiry", label: "Inquiry", accent: "border-[#1B2B4B]/40" },
  { key: "Negotiation", label: "Negotiation", accent: "border-amber-300" },
  { key: "Closed", label: "Closed", accent: "border-[#35622F]/50" },
];

const PIPELINE_ORDER = ["Pending", "Inquiry", "Negotiation", "Closed"];
const NEXT_STATUS = {
  Pending: "Inquiry",
  Inquiry: "Negotiation",
  Negotiation: "Closed",
};
const PREV_STATUS = {
  Inquiry: "Pending",
  Negotiation: "Inquiry",
  // Closed has no PREV — Closed is final.
};

// Client-side mirror of the server rules: adjacent (±1) movement only.
// Closed is final in both directions.
function isValidMove(fromStatus, toStatus) {
  if (fromStatus === toStatus) return false;
  if (fromStatus === "Closed") return false;
  const fromIdx = PIPELINE_ORDER.indexOf(fromStatus);
  const toIdx = PIPELINE_ORDER.indexOf(toStatus);
  if (fromIdx < 0 || toIdx < 0) return false;
  return Math.abs(fromIdx - toIdx) === 1;
}

function MatchScoreDot({ score }) {
  const color = score >= 80 ? "#35622F" : "#1B2B4B";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold"
      style={{ borderColor: `${color}40`, color, backgroundColor: `${color}12` }}
    >
      {score}% match
    </span>
  );
}

function renderAdvanceButtonInner(working, isLastStep, next) {
  if (working) {
    return (
      <>
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        Moving…
      </>
    );
  }
  if (isLastStep) {
    return (
      <>
        <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
        Close deal
      </>
    );
  }
  return (
    <>
      Advance to {next}
      <ArrowRight className="ml-2 h-3.5 w-3.5" />
    </>
  );
}

function DraggableCard({ match, onAdvance, onMoveBack, working, onRequestClose, showReward }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: match.id,
    data: { status: match.status },
    disabled: match.status === "Closed",
  });

  const next = NEXT_STATUS[match.status];
  const prev = PREV_STATUS[match.status];
  const isClosed = match.status === "Closed";
  const isLastStep = next === "Closed";

  return (
    <motion.div
      ref={setNodeRef}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: isDragging ? 0 : 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid={`pipeline-card-${match.id}`}
    >
      <div
        className={`flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-mb-card transition ${
          isClosed ? "cursor-default" : "cursor-grab active:cursor-grabbing"
        } hover:-translate-y-[1px] hover:shadow-mb-card-hover`}
        {...(isClosed ? {} : { ...listeners, ...attributes })}
      >
        <div className="flex items-start justify-between gap-2">
          <MatchScoreDot score={match.match_score} />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {match.is_own_listing ? "Your listing" : "Your buyer"}
          </span>
        </div>

        <div className="min-w-0">
          <div
            className="truncate text-sm font-semibold text-gray-900"
            data-testid={`pipeline-card-title-${match.id}`}
          >
            {match.listing.title}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin className="h-3 w-3 text-gray-400" />
            <span className="truncate">{match.listing.location}</span>
          </div>
          <div className="mt-1 text-base font-bold tracking-tight text-gray-900">
            {formatPhpCompact(match.listing.price_gross ?? match.listing.price)}
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="font-medium">with</span>
            <span
              className="font-semibold text-gray-900"
              data-testid={`pipeline-card-counterpart-${match.id}`}
            >
              {match.counterpart.full_name}
            </span>
            {match.counterpart.is_verified && (
              <BadgeCheck className="h-3 w-3 text-[#35622F]" />
            )}
          </div>
          <div className="mt-1">
            <CoBrokerScorePill score={match.counterpart_score} />
          </div>
        </div>

        {!isClosed && next && (
          <div className="flex flex-col gap-1.5">
            <Button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                isLastStep ? onRequestClose(match) : onAdvance(match, next);
              }}
              disabled={working}
              className="mb-focus-ring h-9 rounded-xl bg-[#1B2B4B] text-xs font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
              data-testid={`advance-match-button-${match.id}`}
            >
              {renderAdvanceButtonInner(working, isLastStep, next)}
            </Button>
            {prev && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBack(match, prev);
                }}
                disabled={working}
                className="mb-focus-ring inline-flex h-7 items-center justify-center gap-1 rounded-lg text-[11px] font-medium text-gray-500 hover:text-gray-800 disabled:opacity-60"
                data-testid={`move-back-button-${match.id}`}
              >
                <ArrowLeft className="h-3 w-3" />
                Move back to {prev}
              </button>
            )}
          </div>
        )}
        {isClosed && (
          <div className="mt-1 inline-flex items-center justify-center gap-1 rounded-lg border border-[#35622F]/25 bg-[#35622F]/10 px-2 py-1 text-[11px] font-semibold text-[#35622F]">
            <CheckCircle2 className="h-3 w-3" />
            Deal closed
          </div>
        )}
      </div>

      <AnimatePresence>
        {showReward && (
          <motion.div
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid={`pipeline-card-reward-${match.id}`}
          >
            <motion.span
              className="absolute inset-0 rounded-2xl bg-[#35622F]/10"
              initial={{ scale: 0.98 }}
              animate={{ scale: [0.98, 1.02, 1] }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
            <motion.span
              className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg"
              initial={{ scale: 0, rotate: -12 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
            >
              <CheckCircle2 className="h-8 w-8 text-[#35622F]" />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Column({ col, children }) {
  const { setNodeRef, isOver, active } = useDroppable({ id: `col:${col.key}` });
  const activeFromStatus = active?.data?.current?.status;
  const wouldAccept =
    active && activeFromStatus && isValidMove(activeFromStatus, col.key);
  const wouldReject = active && !wouldAccept && activeFromStatus !== col.key;

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[240px] flex-col gap-3 rounded-2xl border p-4 transition ${
        isOver && wouldAccept
          ? "border-[#35622F] bg-[#35622F]/[0.06] ring-2 ring-[#35622F]/25"
          : isOver && wouldReject
            ? "border-red-200 bg-red-50/40"
            : "border-gray-100 bg-gray-50/60"
      }`}
      data-testid={`pipeline-column-${col.key.toLowerCase()}`}
      data-drop-state={
        isOver && wouldAccept ? "accept" : isOver && wouldReject ? "reject" : "idle"
      }
    >
      <div className={`flex items-center justify-between border-b pb-2 ${col.accent}`}>
        <div className="text-sm font-bold text-gray-900">{col.label}</div>
        <span
          className="text-xs font-semibold text-gray-500"
          data-testid={`pipeline-column-count-${col.key.toLowerCase()}`}
        >
          {col.items.length}
        </span>
      </div>
      {children}
    </div>
  );
}

function ConfirmCloseDialog({ match, onConfirm, onCancel, working }) {
  const open = !!match;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent
        className="max-w-md rounded-2xl border-gray-200 bg-white p-8"
        data-testid="close-deal-dialog"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#35622F]/10">
          <CheckCircle2 className="h-5 w-5 text-[#35622F]" />
        </div>
        <DialogTitle
          className="mt-5 text-center text-xl font-bold tracking-tight text-gray-900"
          data-testid="close-deal-title"
        >
          Close this deal?
        </DialogTitle>
        <DialogDescription className="mt-2 text-center text-sm text-gray-600">
          The listing will be <b>archived</b> and the buyer requirement
          <b> closed</b>. Both brokers will be notified. This action can&apos;t
          be reversed.
        </DialogDescription>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            data-testid="close-deal-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={working}
            className="mb-focus-ring h-10 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
            data-testid="close-deal-confirm"
          >
            {working ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Closing…
              </>
            ) : (
              "Close deal"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PipelinePage() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [pendingClose, setPendingClose] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [rewardId, setRewardId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const reduced = useReducedMotion();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches/mine");
      setMatches(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load pipeline"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doAdvance = useCallback(
    async (match, targetStatus) => {
      setWorkingId(match.id);
      try {
        await api.patch(`/matches/${match.id}/status`, { status: targetStatus });
        setMatches((prev) =>
          prev.map((m) => (m.id === match.id ? { ...m, status: targetStatus } : m))
        );
        if (targetStatus === "Closed") {
          setRewardId(match.id);
          setTimeout(() => setRewardId(null), 900);
        }
        toast.success(
          targetStatus === "Closed"
            ? "Deal closed. Both brokers notified."
            : `Moved to ${targetStatus}.`
        );
        if (targetStatus === "Closed") load(); // pick up side-effects
      } catch (e) {
        const s = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (s === 402 && detail === "UPGRADE_REQUIRED") {
          setPendingClose(null);
          setShowUpgrade(true);
        } else {
          toast.error(formatApiError(e, "Transition failed"));
        }
      } finally {
        setWorkingId(null);
        setPendingClose(null);
      }
    },
    [load]
  );

  const handleDragStart = (event) => {
    setActiveId(event.active?.id ?? null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const droppedOn = String(over.id);
    if (!droppedOn.startsWith("col:")) return;
    const targetStatus = droppedOn.slice(4);
    const match = matches.find((m) => m.id === active.id);
    if (!match) return;
    if (!isValidMove(match.status, targetStatus)) {
      // Invalid — surface why. framer-motion springs the card back automatically
      // because we don't mutate state; the DragOverlay disappears and the source
      // card in the origin column resumes its layout position.
      if (match.status === "Closed") {
        toast.error("Closed deals can't be moved.");
      } else if (targetStatus === match.status) {
        // no-op — dropped in same column
      } else {
        toast.error("Deals move one stage at a time — try an adjacent column.");
      }
      return;
    }
    // Valid move — Closed still asks for confirmation before firing the PATCH.
    if (targetStatus === "Closed") {
      setPendingClose(match);
      return;
    }
    doAdvance(match, targetStatus);
  };

  const columns = COLUMNS.map((col) => ({
    ...col,
    items: matches.filter((m) => m.status === col.key),
  }));

  const activeMatch = activeId ? matches.find((m) => m.id === activeId) : null;

  return (
    <section
      className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="page-pipeline"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            Pipeline
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Deal pipeline
          </h1>
          <p
            className="mt-3 max-w-xl text-base leading-relaxed text-gray-500"
            data-testid="pipeline-count"
          >
            {loading
              ? "Loading pipeline…"
              : `${matches.length} deal${matches.length === 1 ? "" : "s"} in flight · drag to advance`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          data-testid="pipeline-refresh-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div
              className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
              data-testid="pipeline-loading-skeleton"
            >
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col gap-3">
                  <SkeletonCard lines={2} />
                  {i < 2 && <SkeletonCard lines={2} />}
                </div>
              ))}
            </div>
          );
        }
        if (matches.length === 0) {
          return (
            <div
              className="mt-10 rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-mb-card"
              data-testid="pipeline-empty-state"
            >
              <h3 className="text-xl font-semibold text-gray-900">
                No deals in your pipeline yet.
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
                When you have matches, advance them from{" "}
                <Link
                  to="/dashboard/matches"
                  className="font-semibold text-[#1B2B4B] hover:underline"
                >
                  Matches
                </Link>{" "}
                or drag them right here.
              </p>
            </div>
          );
        }
        return (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div
              className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
              data-testid="pipeline-board"
            >
              {columns.map((col) => (
                <Column key={col.key} col={col}>
                  {col.items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-white/50 px-3 py-6 text-center text-xs text-gray-400">
                      No deals here.
                    </div>
                  ) : (
                    col.items.map((m) => (
                      <DraggableCard
                        key={m.id}
                        match={m}
                        onAdvance={doAdvance}
                        onMoveBack={doAdvance}
                        working={workingId === m.id}
                        onRequestClose={setPendingClose}
                        showReward={rewardId === m.id}
                      />
                    ))
                  )}
                </Column>
              ))}
            </div>

            <DragOverlay
              dropAnimation={{
                duration: reduced ? 0 : 260,
                easing: "cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              {activeMatch ? (
                <div
                  className="w-[280px] rotate-[2deg] scale-[1.02] rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_24px_60px_rgba(0,0,0,0.25)]"
                  data-testid="pipeline-drag-overlay"
                >
                  <div className="flex items-start justify-between gap-2">
                    <MatchScoreDot score={activeMatch.match_score} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      {activeMatch.is_own_listing ? "Your listing" : "Your buyer"}
                    </span>
                  </div>
                  <div className="mt-3 truncate text-sm font-semibold text-gray-900">
                    {activeMatch.listing.title}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray-500">
                    {activeMatch.listing.location}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        );
      })()}

      <ConfirmCloseDialog
        match={pendingClose}
        working={workingId === pendingClose?.id}
        onConfirm={() => pendingClose && doAdvance(pendingClose, "Closed")}
        onCancel={() => setPendingClose(null)}
      />
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </section>
  );
}
